(() => {
  const TAG_NAME = 'condition-variant-cards';
  const READY_CLASS = 'is-ready';
  const NATIVE_GROUP_CLASS = 'cvc-native-picker--hidden';
  const NATIVE_CONTROL_CLASS = 'cvc-native-control--hidden';
  const NATIVE_LABEL_CLASS = 'cvc-native-label--hidden';

  if (customElements.get(TAG_NAME)) return;

  const normalize = (value) =>
    String(value ?? '')
      .trim()
      .replace(/\s+/g, ' ')
      .toLocaleLowerCase();

  const isElement = (value) => value instanceof Element;

  class ConditionVariantCards extends HTMLElement {
    constructor() {
      super();
      this.onClick = this.onClick.bind(this);
      this.onKeyDown = this.onKeyDown.bind(this);
      this.onNativeChange = this.onNativeChange.bind(this);
      this.onVariantChange = this.onVariantChange.bind(this);
      this.onMutations = this.onMutations.bind(this);
    }

    connectedCallback() {
      if (this.isConnectedAndBound) return;
      this.isConnectedAndBound = true;

      this.cards = Array.from(this.querySelectorAll('[data-cvc-card]'));
      if (!this.cards.length) return;

      this.scope =
        document.getElementById(`shopify-section-${this.dataset.sectionId}`) ||
        this.closest('[id^="shopify-section-"]') ||
        document;

      this.nativeByVariantId = new Map();
      this.hiddenNativeElements = new Set();
      this.refreshTimer = null;
      this.fallbackTimer = null;
      this.pendingVariantId = '';

      this.addEventListener('click', this.onClick);
      this.addEventListener('keydown', this.onKeyDown);
      this.scope.addEventListener('change', this.onNativeChange, true);
      document.addEventListener('product:variant-change', this.onVariantChange);

      this.observer = new MutationObserver(this.onMutations);
      this.observer.observe(this.scope, {
        childList: true,
        subtree: true,
      });

      requestAnimationFrame(() => this.initialize(0));
    }

    disconnectedCallback() {
      this.isConnectedAndBound = false;
      this.removeEventListener('click', this.onClick);
      this.removeEventListener('keydown', this.onKeyDown);
      this.scope?.removeEventListener('change', this.onNativeChange, true);
      document.removeEventListener('product:variant-change', this.onVariantChange);
      this.observer?.disconnect();
      window.clearTimeout(this.refreshTimer);
      window.clearTimeout(this.fallbackTimer);

      if (window.Shopify?.designMode) this.restoreNativePicker();
    }

    initialize(attempt = 0) {
      if (!this.isConnected) return;

      const binding = this.findBestNativeBinding();

      if (!binding) {
        if (attempt < 20) {
          window.setTimeout(() => this.initialize(attempt + 1), 100);
        }
        return;
      }

      this.nativeByVariantId = binding.matches;
      this.nativeGroup = binding.group;
      this.hideNativePicker();
      this.syncFromNativePicker();
      this.classList.add(READY_CLASS);
    }

    onMutations(mutations) {
      const changed = mutations.some(
        (mutation) =>
          mutation.type === 'childList' &&
          (mutation.addedNodes.length || mutation.removedNodes.length),
      );

      if (changed && this.isConnected) this.refreshNativeBinding();
    }

    scheduleRefresh(delay = 0) {
      window.clearTimeout(this.refreshTimer);
      this.refreshTimer = window.setTimeout(() => {
        if (!this.isConnected) return;
        this.refreshNativeBinding();
      }, delay);
    }

    refreshNativeBinding() {
      const binding = this.findBestNativeBinding();
      if (!binding) return;

      this.nativeByVariantId = binding.matches;
      this.nativeGroup = binding.group;
      this.hideNativePicker();
      this.syncFromNativePicker();
      this.classList.add(READY_CLASS);
    }

    getDocumentOrderMap() {
      const elements = Array.from(this.scope.querySelectorAll('*'));
      const order = new Map(elements.map((element, index) => [element, index]));

      return {
        order,
        referenceIndex: order.get(this) ?? 0,
      };
    }

    getControlLabel(control) {
      if (!(control instanceof HTMLInputElement) || !control.id) return null;

      const labels = Array.from(this.scope.querySelectorAll('label[for]'));
      return labels.find((label) => label.htmlFor === control.id) || null;
    }

    getNativeGroup(control) {
      const group =
        control.closest('fieldset') ||
        control.closest('[role="radiogroup"]') ||
        control.closest('[data-option-position]') ||
        control.closest('[data-option-index]') ||
        control.closest('[data-product-option]') ||
        control.closest('[data-variant-option]') ||
        control.closest('[data-option-name]') ||
        control.closest('.product-form__input') ||
        control.closest('.product-form__item') ||
        control.closest('.product-form__option') ||
        control.closest('.product-form__options') ||
        control.closest('.product-form__controls-group') ||
        control.closest('.product-options') ||
        control.closest('.product-option') ||
        control.closest('.variant-picker__values') ||
        control.closest('.variant-selector__values') ||
        control.closest('.selector-wrapper') ||
        control.closest('.variant-picker') ||
        control.closest('.variant-selector') ||
        control.closest('.variant-wrapper') ||
        control.closest('.variant-input-wrap') ||
        control.closest('variant-selects') ||
        control.closest('variant-radios') ||
        control.closest('product-variants') ||
        control.closest('product-variant-picker');

      if (group && !group.contains(this)) return group;
      return control.parentElement;
    }

    isExcludedControl(control) {
      if (!isElement(control)) return true;
      if (this.contains(control) || control.closest(TAG_NAME)) return true;

      const name = normalize(control.getAttribute('name'));

      // A product form's name="id" field contains variant IDs. It is not the
      // option picker and must never be used to drive Athens' variant logic.
      if (name === 'id' || name === 'quantity') return true;

      if (
        control.closest('[data-cvc-ignore]') ||
        control.closest('bulk-order') ||
        control.closest('quick-order-list')
      ) {
        return true;
      }

      return false;
    }

    getCandidateControls() {
      const selector = [
        'input[type="radio"]',
        'select',
        'button[data-option-value]',
        'button[data-value]',
        '[role="radio"][data-option-value]',
        '[role="radio"][data-value]',
      ].join(',');

      return Array.from(this.scope.querySelectorAll(selector)).filter(
        (control) => !this.isExcludedControl(control),
      );
    }

    matchControl(control, optionValue) {
      const target = normalize(optionValue);
      if (!target) return null;

      if (control instanceof HTMLInputElement) {
        const label = this.getControlLabel(control);
        const possibleValues = [
          control.value,
          control.dataset.value,
          control.dataset.optionValue,
          control.getAttribute('aria-label'),
          label?.dataset.value,
          label?.dataset.optionValue,
          label?.getAttribute('aria-label'),
          label?.textContent,
        ];

        if (!possibleValues.some((value) => normalize(value) === target)) {
          return null;
        }

        return {
          control,
          label,
          nativeValue: control.value,
          type: 'radio',
        };
      }

      if (control instanceof HTMLSelectElement) {
        const option = Array.from(control.options).find((item) => {
          const possibleValues = [
            item.value,
            item.textContent,
            item.dataset.value,
            item.dataset.optionValue,
          ];

          return possibleValues.some((value) => normalize(value) === target);
        });

        if (!option) return null;

        return {
          control,
          option,
          nativeValue: option.value,
          type: 'select',
        };
      }

      const possibleValues = [
        control.getAttribute('value'),
        control.dataset.value,
        control.dataset.optionValue,
        control.getAttribute('aria-label'),
        control.textContent,
      ];

      if (!possibleValues.some((value) => normalize(value) === target)) {
        return null;
      }

      return {
        control,
        nativeValue:
          control.dataset.optionValue ||
          control.dataset.value ||
          control.getAttribute('value') ||
          optionValue,
        type: 'button',
      };
    }

    getMatchScore(match, group, order, referenceIndex) {
      const control = match.control;
      const name = normalize(control.getAttribute('name'));
      const optionName = normalize(this.dataset.optionName);
      const controlIndex = order.get(control) ?? Number.MAX_SAFE_INTEGER;
      const distance = Math.abs(controlIndex - referenceIndex);

      let score = distance;

      if (match.type === 'radio') score -= 10000;
      if (match.type === 'button') score -= 7500;
      if (match.type === 'select') score -= 5000;

      if (name.startsWith('options[') || name.includes('option')) score -= 2500;
      if (optionName && name.includes(optionName)) score -= 1500;

      if (
        control.closest(
          'variant-selects, variant-radios, product-variants, product-variant-picker, fieldset, [data-option-position], [data-product-option], [data-variant-option]',
        )
      ) {
        score -= 2500;
      }

      if (group?.tagName === 'FIELDSET') score -= 500;

      return score;
    }

    findBestNativeBinding() {
      const selectableCards = this.cards.filter((card) => !card.disabled);
      if (!selectableCards.length) return null;

      const controls = this.getCandidateControls();
      if (!controls.length) return null;

      const { order, referenceIndex } = this.getDocumentOrderMap();
      const entries = [];

      selectableCards.forEach((card) => {
        controls.forEach((control) => {
          const match = this.matchControl(control, card.dataset.optionValue);
          if (!match) return;

          const group = this.getNativeGroup(control);
          if (!group || group.contains(this)) return;

          entries.push({
            card,
            group,
            match,
            score: this.getMatchScore(match, group, order, referenceIndex),
          });
        });
      });

      if (!entries.length) return null;

      const groups = Array.from(new Set(entries.map((entry) => entry.group)));
      const completeBindings = [];

      groups.forEach((group) => {
        const matches = new Map();
        let totalScore = 0;

        for (const card of selectableCards) {
          const candidates = entries
            .filter((entry) => entry.group === group && entry.card === card)
            .sort((a, b) => a.score - b.score);

          if (!candidates.length) return;

          const best = candidates[0];
          matches.set(card.dataset.variantId, best.match);
          totalScore += best.score;
        }

        const uniqueControls = new Set(
          Array.from(matches.values()).map((match) => match.control),
        );

        // Radio/button pickers need one control per card. A select picker uses
        // one shared select with a different option value for each card.
        const firstType = Array.from(matches.values())[0]?.type;
        if (firstType !== 'select' && uniqueControls.size !== selectableCards.length) {
          return;
        }

        completeBindings.push({
          group,
          matches,
          score: totalScore,
        });
      });

      completeBindings.sort((a, b) => a.score - b.score);
      return completeBindings[0] || null;
    }

    restoreNativePicker() {
      if (!this.hiddenNativeElements) return;

      this.hiddenNativeElements.forEach((element) => {
        if (!element.isConnected) return;
        element.classList.remove(
          NATIVE_GROUP_CLASS,
          NATIVE_CONTROL_CLASS,
          NATIVE_LABEL_CLASS,
        );
      });
      this.hiddenNativeElements.clear();
    }

    hideNativePicker() {
      // Keep previously matched groups hidden. Athens can replace or duplicate
      // picker markup during an AJAX update; removing the old class first can
      // make the original picker flash or remain visible.
      this.hiddenNativeElements.forEach((element) => {
        if (!element.isConnected) this.hiddenNativeElements.delete(element);
      });

      const matches = Array.from(this.nativeByVariantId.values());
      const controls = Array.from(new Set(matches.map((match) => match.control)));
      const labels = Array.from(
        new Set(matches.map((match) => match.label).filter(Boolean)),
      );

      if (
        this.nativeGroup &&
        !this.nativeGroup.contains(this) &&
        controls.every((control) => this.nativeGroup.contains(control))
      ) {
        this.nativeGroup.classList.add(NATIVE_GROUP_CLASS);
        this.hiddenNativeElements.add(this.nativeGroup);
        return;
      }

      controls.forEach((control) => {
        control.classList.add(NATIVE_CONTROL_CLASS);
        this.hiddenNativeElements.add(control);
      });

      labels.forEach((label) => {
        label.classList.add(NATIVE_LABEL_CLASS);
        this.hiddenNativeElements.add(label);
      });
    }

    onClick(event) {
      const card = event.target.closest('[data-cvc-card]');
      if (!card || !this.contains(card) || card.disabled) return;
      this.selectCard(card);
    }

    onKeyDown(event) {
      const currentCard = event.target.closest('[data-cvc-card]');
      if (!currentCard || !this.contains(currentCard)) return;

      const enabledCards = this.cards.filter((card) => !card.disabled);
      if (!enabledCards.length) return;

      const currentIndex = Math.max(enabledCards.indexOf(currentCard), 0);
      let nextIndex = null;

      switch (event.key) {
        case 'ArrowRight':
        case 'ArrowDown':
          nextIndex = (currentIndex + 1) % enabledCards.length;
          break;
        case 'ArrowLeft':
        case 'ArrowUp':
          nextIndex =
            (currentIndex - 1 + enabledCards.length) % enabledCards.length;
          break;
        case 'Home':
          nextIndex = 0;
          break;
        case 'End':
          nextIndex = enabledCards.length - 1;
          break;
        default:
          return;
      }

      event.preventDefault();
      const nextCard = enabledCards[nextIndex];
      nextCard.focus();
      this.selectCard(nextCard);
    }

    selectCard(card) {
      const variantId = String(card.dataset.variantId || '');
      const match = this.nativeByVariantId.get(variantId);

      if (!match) {
        this.reloadWithVariant(variantId);
        return;
      }

      this.setSelectedCard(card);
      this.pendingVariantId = variantId;
      this.activateNativeControl(match);

      // Let Athens receive the native option change first. Then synchronize the
      // add-to-cart form's variant ID as a secondary safeguard.
      queueMicrotask(() => this.updateProductFormVariantId(variantId));

      // Athens may replace some variant-picker HTML during its AJAX update.
      // Re-scan and re-hide the native picker after those replacements.
      this.scheduleRefresh(0);
      window.setTimeout(() => this.scheduleRefresh(0), 100);
      window.setTimeout(() => this.scheduleRefresh(0), 350);

      this.startFallbackTimer(variantId);
    }

    activateNativeControl(match) {
      const control = match.control;

      if (control instanceof HTMLInputElement) {
        if (control.checked) {
          control.dispatchEvent(new Event('change', { bubbles: true }));
          return;
        }

        let changeFired = false;
        const markChange = () => {
          changeFired = true;
        };

        control.addEventListener('change', markChange, { once: true });
        const clickTarget = match.label?.isConnected ? match.label : control;
        clickTarget.click();
        control.removeEventListener('change', markChange);

        if (!control.checked) control.checked = true;

        if (!changeFired) {
          control.dispatchEvent(new Event('input', { bubbles: true }));
          control.dispatchEvent(new Event('change', { bubbles: true }));
        }
        return;
      }

      if (control instanceof HTMLSelectElement) {
        if (control.value !== match.nativeValue) {
          control.value = match.nativeValue;
        }
        control.dispatchEvent(new Event('input', { bubbles: true }));
        control.dispatchEvent(new Event('change', { bubbles: true }));
        return;
      }

      control.click();
    }

    updateProductFormVariantId(variantId) {
      const forms = Array.from(
        this.scope.querySelectorAll('form[action*="/cart/add"]'),
      );

      forms.forEach((form) => {
        if (form.closest('bulk-order, quick-order-list')) return;

        const field = form.querySelector('[name="id"]');
        if (!field) return;

        if (field instanceof HTMLSelectElement) {
          const hasVariant = Array.from(field.options).some(
            (option) => String(option.value) === variantId,
          );
          if (hasVariant) field.value = variantId;
          return;
        }

        if (field instanceof HTMLInputElement) {
          field.value = variantId;
        }
      });
    }

    startFallbackTimer(variantId) {
      window.clearTimeout(this.fallbackTimer);

      if (window.Shopify?.designMode) return;

      this.fallbackTimer = window.setTimeout(() => {
        if (this.pendingVariantId !== variantId) return;

        // Athens normally fires product:variant-change after updating price,
        // SKU, media and availability. If that never happens, use Shopify's
        // variant deep link as a safe functional fallback.
        this.reloadWithVariant(variantId);
      }, 2500);
    }

    setSelectedCard(selectedCard) {
      this.cards.forEach((card) => {
        const selected = card === selectedCard;
        card.classList.toggle('is-selected', selected);
        card.setAttribute('aria-checked', selected ? 'true' : 'false');
        card.tabIndex = selected ? 0 : -1;
      });
    }

    syncFromNativePicker() {
      for (const card of this.cards) {
        const match = this.nativeByVariantId.get(card.dataset.variantId);
        if (!match) continue;

        if (match.control instanceof HTMLInputElement && match.control.checked) {
          this.setSelectedCard(card);
          return;
        }

        if (
          match.control instanceof HTMLSelectElement &&
          match.control.value === match.nativeValue
        ) {
          this.setSelectedCard(card);
          return;
        }

        if (
          !(match.control instanceof HTMLInputElement) &&
          !(match.control instanceof HTMLSelectElement) &&
          (match.control.getAttribute('aria-checked') === 'true' ||
            match.control.getAttribute('aria-pressed') === 'true' ||
            match.control.classList.contains('is-selected') ||
            match.control.classList.contains('selected') ||
            match.control.classList.contains('active'))
        ) {
          this.setSelectedCard(card);
          return;
        }
      }
    }

    onNativeChange(event) {
      const isKnownControl = Array.from(this.nativeByVariantId.values()).some(
        (match) => match.control === event.target,
      );

      if (!isKnownControl) return;
      this.syncFromNativePicker();
      this.scheduleRefresh(50);
    }

    onVariantChange(event) {
      const variantId = String(event.detail?.variant?.id ?? '');
      if (!variantId) return;

      const card = this.cards.find(
        (item) => String(item.dataset.variantId) === variantId,
      );
      if (!card) return;

      window.clearTimeout(this.fallbackTimer);
      this.pendingVariantId = '';
      this.setSelectedCard(card);
      this.updateProductFormVariantId(variantId);

      this.scheduleRefresh(0);
      window.setTimeout(() => this.scheduleRefresh(0), 100);
      window.setTimeout(() => this.scheduleRefresh(0), 350);
    }

    reloadWithVariant(variantId) {
      if (!variantId) return;
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variantId);
      window.location.assign(url.toString());
    }
  }

  customElements.define(TAG_NAME, ConditionVariantCards);
})();
